import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import DOMPurify from 'dompurify';

import { checkIsDarkTheme } from 'utils/theme';

import { BlackColorVariations, WhiteColorVariations } from '../../Activities.constants';

import { CLASSES_TO_SKIP_INVERT_COLORS } from './constants';
import { highlightTextInElements } from './utils';

const useHtmlAdjustedToTheme = (htmlContent, contentMap, highlightText) => {
  const theme = useTheme();
  const isDarkTheme = checkIsDarkTheme(theme.palette.type);

  const adjustedHtml = useMemo(() => {
    const purifiedHtmlContent = DOMPurify.sanitize(htmlContent, {
      WHOLE_DOCUMENT: true,
    });

    const htmlWrapper = document.createElement('html');
    htmlWrapper.innerHTML = purifiedHtmlContent;
    const elementsArray = Array.from(htmlWrapper.querySelectorAll('* :not(style)'));

    elementsArray.forEach(element => {
      if (
        contentMap &&
        element.localName === 'img' &&
        element.dataset.source &&
        element.dataset.source.startsWith('cid:')
      ) {
        const resourceId = element.dataset.source.split('cid:')[1];

        if (contentMap[resourceId]) {
          element.src = contentMap[resourceId];
        }
      }

      if (!element.style.background && !element.style.backgroundColor) {
        const textColorSetByAttribute = element.getAttribute('color');

        const isWhiteOnDark =
          isDarkTheme &&
          (BlackColorVariations.includes(element.style.color) ||
            BlackColorVariations.includes(textColorSetByAttribute));

        const isBlackOnLight =
          !isDarkTheme &&
          (WhiteColorVariations.includes(element.style.color) ||
            WhiteColorVariations.includes(textColorSetByAttribute));

        const shouldSkipInvert = Array.from(element.classList).some(name =>
          CLASSES_TO_SKIP_INVERT_COLORS.includes(name),
        );

        if (!shouldSkipInvert && (isWhiteOnDark || isBlackOnLight)) {
          element.style.color = theme.palette.text.primary;
        }
      }
    });

    if (highlightText) {
      highlightTextInElements(elementsArray, highlightText);
    }

    return { __html: htmlWrapper.innerHTML };
  }, [htmlContent, highlightText, contentMap, isDarkTheme, theme.palette.text.primary]);

  return adjustedHtml;
};

export default useHtmlAdjustedToTheme;
